import React from 'react';
import { Callout, Kbd, List, Only, P, Steps, Sub, Table, UI } from '../ui';
import { useUploadPolicy } from '../../../services/uploadPolicyService';
import { useBlockedExtensions } from '../../../services/blockedExtensionService';

const ExtList: React.FC<{ items: string[] }> = ({ items }) => (
  <div className="flex flex-wrap gap-1.5 my-2">
    {items.map((ext) => (
      <code
        key={ext}
        className="px-1.5 py-0.5 text-[12px] font-mono text-brand-700 bg-brand-50 border border-brand-200 rounded"
      >
        {ext}
      </code>
    ))}
  </div>
);

export const Files: React.FC = () => {
  // 첨부 제한·차단 목록은 관리자 콘솔에서 바뀔 수 있다. 여기 적어두면 조용히 어긋난다.
  const { maxFileSizeMB, maxFiles, retentionDays } = useUploadPolicy();
  const { managed, critical, loaded } = useBlockedExtensions();

  return (
  <>
    <Sub>파일 첨부하는 네 가지 방법</Sub>
    <Table
      head={['방법', '조작']}
      rows={[
        [
          '첨부 버튼',
          <>
            입력창 왼쪽의 클립 버튼을 누르면 <UI>파일 첨부</UI>, <UI>이미지 선택</UI>, <UI>화면 캡처</UI>(PC)
            메뉴가 나옵니다
          </>,
        ],
        ['끌어다 놓기', '탐색기에서 파일을 집어 대화창 위에 놓습니다. 놓을 수 있는 영역이 파란 점선으로 표시됩니다'],
        [
          '붙여넣기',
          <>
            캡처한 이미지를 복사한 뒤 입력창에서 <Kbd>Ctrl</Kbd> + <Kbd>V</Kbd> 를 누릅니다. 엑셀 표를
            복사한 경우에는 첨부가 아니라 <b>표 모양 글자</b>로 들어갑니다
          </>,
        ],
        ['화면 캡처', 'PC에서 앱 안에서 바로 화면을 잘라 첨부합니다 (아래 참고)'],
      ]}
    />
    <P>
      첨부한 파일은 입력창 위에 미리보기 카드로 쌓입니다. 이미지는 작은 그림으로, 그 외 파일은 아이콘과
      파일명·크기로 표시되며 카드의 <UI>×</UI>로 하나씩 뺄 수 있습니다. 글 없이 파일만 보낼 수도 있습니다.
    </P>

    <Sub>화면 캡처로 바로 보내기</Sub>
    <Steps>
      <>
        클립 버튼 → <UI>화면 캡처</UI>를 선택합니다.
      </>
      <>공유할 화면이나 창을 고릅니다.</>
      <>
        화면이 어두워지면 <b>마우스로 드래그해서 보낼 영역을 지정</b>합니다. 드래그하는 동안 선택 크기가
        표시됩니다.
      </>
      <>
        확인 버튼을 누르면 첨부 목록에 들어갑니다. 취소하려면 <Kbd>Esc</Kbd> 를 누르세요.
      </>
    </Steps>
    <P>
      휴대폰에는 이 메뉴가 없습니다. 휴대폰의 스크린샷 기능으로 화면을 찍은 뒤 클립 버튼 → <UI>이미지 선택</UI>으로
      보내세요.
    </P>

    <Sub>첨부 제한</Sub>
    <Table
      head={['항목', '제한']}
      rows={[
        ['파일 하나당 크기', <b key="a">최대 {maxFileSizeMB}MB</b>],
        ['한 번에 보낼 개수', <b key="b">최대 {maxFiles}개</b>],
        ['첨부 보관 기간', <b key="c">{retentionDays > 0 ? `${retentionDays}일` : '만료 없음'}</b>],
        ['붙여넣기로 넣을 수 있는 이미지', 'PNG, JPEG, GIF, WebP'],
        ['붙여넣을 수 있는 표 크기', <b key="d">최대 100행 30열</b>],
      ]}
    />
    {retentionDays > 0 && (
      <Callout tone="info" title="첨부는 보관 기간이 지나면 사라집니다">
        <p>
          보낸 파일과 사진은 <b>{retentionDays}일</b>이 지나면 서버에서 자동으로 삭제됩니다. 첨부 카드에 언제까지
          받을 수 있는지 표시되고, 기간이 지난 뒤에는 <b>유효기간이 지나 다운로드할 수 없습니다</b>로 바뀝니다.
          지워진 파일은 되살릴 수 없으니, 오래 두고 볼 자료는 미리 내려받거나 사내 공유 폴더에 보관하세요.
          이미 보낸 첨부의 만료일은 관리자가 기간을 바꿔도 달라지지 않습니다.
        </p>
      </Callout>
    )}
    <Callout tone="info" title="큰 파일은 첨부하는 순간 걸러집니다">
      <p>
        {maxFileSizeMB}MB가 넘는 파일은 고르거나 끌어다 놓는 즉시 안내가 표시되고 첨부 목록에 들어가지 않습니다.
        업로드가 끝나기를 기다릴 필요가 없습니다. 여러 개를 한꺼번에 골랐다면 용량을 넘긴 파일만 빠지고 나머지는
        그대로 첨부됩니다. 용량이 큰 자료는 사내 공유 폴더를 이용하시는 편이 빠릅니다.
      </p>
    </Callout>

    <Sub>보낼 수 없는 파일 형식</Sub>
    <P>
      악성코드 유포를 막기 위해 실행 파일과 스크립트 계열은 첨부가 차단됩니다. 차단된 형식을 올리면{' '}
      <UI>보안 정책으로 인해 ○○ 확장자 파일은 첨부할 수 없습니다.</UI> 라는 안내가 표시됩니다.
    </P>
    {!loaded ? (
      <P>차단 목록을 불러오는 중입니다</P>
    ) : (
      <>
        {managed.length > 0 && (
          <>
            <P className="font-semibold text-slate-800">보안 정책상 차단</P>
            <ExtList items={managed} />
          </>
        )}
        {critical.length > 0 && (
          <>
            <P className="font-semibold text-slate-800">항상 차단 (해제 불가)</P>
            <ExtList items={critical} />
          </>
        )}
      </>
    )}
    <Callout tone="info" title="그 외 형식은 모두 보낼 수 있습니다">
      <p>
        한글(.hwp), 워드·엑셀·파워포인트, PDF, 압축파일(.zip, .7z), 동영상(.mp4) 등 위 목록에 없는 형식은 자유롭게
        첨부할 수 있습니다.
      </p>
      <p>
        다만 <b>이미지 파일 중 .svg는 첨부할 수 없습니다.</b> PNG나 JPG로 바꿔서 보내주세요.
      </p>
    </Callout>

    <Sub>받은 이미지 보기</Sub>
    <List>
      <>여러 장을 한 번에 보내면 장수에 맞춰 격자로 정리됩니다. 10장 이상이면 9장까지 보이고 나머지는 +숫자로 표시됩니다.</>
      <>
        이미지를 클릭하면 <b>크게 보기 창</b>이 열립니다. 같은 메시지에 있는 이미지끼리 좌우로 넘길 수 있습니다.
      </>
      <>마우스 휠을 굴리거나 더블클릭하면 최대 4배까지 확대됩니다.</>
      <>크게 보기 창 위쪽의 내려받기 버튼으로 저장할 수 있습니다.</>
    </List>

    <Sub>파일 내려받기</Sub>
    <P>
      이미지가 아닌 파일은 파일명과 크기가 적힌 카드로 표시됩니다. <b>카드를 클릭하면 내려받아집니다.</b>
    </P>
    <Table
      head={['상황', '동작']}
      rows={[
        [
          <>
            이미지 저장 <Only env="desktop" />
          </>,
          '"다른 이름으로 저장" 창이 열려 저장 위치를 고를 수 있습니다',
        ],
        [
          <>
            일반 파일 <Only env="desktop" />
          </>,
          '기본 웹 브라우저가 열리면서 내려받기가 시작됩니다',
        ],
        [
          <>
            모든 파일 <Only env="web" />
          </>,
          '브라우저의 기본 다운로드 폴더에 저장됩니다',
        ],
      ]}
    />
    <P>
      동영상(.mp4)이나 PDF는 내려받지 않고 브라우저에서 바로 재생·열람됩니다. 대화창 안에서 동영상을 바로 재생하는
      기능은 없습니다.
    </P>
    <Callout tone="tip">
      <p>
        같은 이름의 파일을 여러 사람이 올려도 서로 덮어쓰이지 않으며, 한글 파일명도 그대로 유지됩니다.
      </p>
    </Callout>

    <Sub>사진 인쇄</Sub>
    <P>
      받은 사진은 내려받지 않고 바로 프린터로 보낼 수 있습니다. 인쇄 창에서 프린터·매수·방향을 고른 뒤 인쇄하면
      됩니다.
    </P>
    <List>
      <>
        사진을 눌러 크게 본 화면에서 위쪽 <b>프린터 버튼</b>을 누릅니다.
      </>
      <>
        대화 속 사진을 <b>오른쪽 클릭</b>(휴대폰은 <b>길게 누르기</b>)하고 <UI>인쇄</UI>를 고릅니다. 사진 여러 장을 한
        번에 보낸 메시지는 사진이 아닌 곳에서 메뉴를 열면 <UI>사진 N장 인쇄</UI>로 전부 인쇄됩니다.
      </>
      <>
        서랍의 <UI>사진</UI> 탭에서 사진 위의 <b>프린터 버튼</b>을 누릅니다.
      </>
    </List>
    <Callout tone="tip">
      <p>
        사진 한 장이 용지 한 장에 맞춰 인쇄됩니다. 가로로 긴 사진은 인쇄 창에서 방향을 <b>가로</b>로 바꾸면 더 크게
        나옵니다. 유효기간이 지난 사진은 인쇄할 수 없습니다.
      </p>
    </Callout>

    <Sub>사진·파일 서랍</Sub>
    <P>
      &ldquo;그 엑셀 어디 있더라&rdquo; 할 때는 대화를 뒤지지 말고 서랍을 여세요. 대화창 오른쪽 위{' '}
      <b>폴더 버튼</b>을 누르면 그 대화방에서 주고받은 <b>사진과 파일이 최신순으로 모아</b> 보입니다.
    </P>
    <List>
      <>
        <UI>사진</UI> 탭은 격자로 보여주며 클릭하면 크게 보기 창이 열립니다. <UI>파일</UI> 탭은 파일명·크기·날짜·보낸
        사람이 함께 표시되고 클릭하면 내려받아집니다.
      </>
      <>
        각 항목의 <b>화살표 버튼</b>을 누르면 그 파일을 주고받던 대화 위치로 바로 이동합니다. 사진에는{' '}
        <b>프린터 버튼</b>도 있어 바로 인쇄할 수 있습니다.
      </>
      <>목록이 길면 아래 <UI>더 보기</UI>로 이어서 볼 수 있습니다. 삭제된 메시지의 첨부는 나오지 않습니다.</>
    </List>
  </>
  );
};
